import { FunctionComponent, useEffect, useRef } from "react";

import { Box, SelectChangeEvent } from "@mui/material";

import { useFilterBoxStateContext } from "./FilterBoxStateContext";
import { FilterRowLayout } from "./FilterRowLayout";
import { FilterTypeSelector } from "./FilterTypeSelector";
import { BaseFilterInput } from "./types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Delete } from "@/icons/outlined";

interface FilterRowProps {
  filterId?: string;
  onCancel?: () => void;
  isReservedList?: boolean;
  rowTrackingId?: string;
  onRowCreated?: (newFilterId: string) => void;
  onRemove?: () => void;
}

export const FilterRow: FunctionComponent<FilterRowProps> = ({
  filterId,
  onCancel,
  isReservedList = false,
  rowTrackingId,
  onRowCreated,
  onRemove,
}) => {
  const {
    filters,
    validFilters,
    addFilter,
    updateFilter,
    removeFilter,
    filterCombinator,
    searchQuery,
    isInitialFilterId,
    registry,
    amplitude,
  } = useFilterBoxStateContext<BaseFilterInput>();
  const { t } = useTranslate();
  const previousValidState = useRef<boolean>(false);
  const hasOpenedValueControlRef = useRef<boolean>(false);
  const hasEnteredValueRef = useRef<boolean>(false);
  const previousConfigIdRef = useRef<string | undefined>(undefined);

  const filter = filterId ? filters.find((f) => f.id === filterId) : undefined;
  const trackingId = rowTrackingId ?? filter?.id;

  if (filter?.configId !== previousConfigIdRef.current) {
    previousConfigIdRef.current = filter?.configId;
    hasOpenedValueControlRef.current = false;
    hasEnteredValueRef.current = false;
  }

  const handleTypeChange = (newConfigId: string) => {
    if (filterId && filter) {
      const newFilter = registry.createDefault(newConfigId);
      updateFilter(filterId, {
        ...newFilter,
        id: filterId,
      });
    } else {
      const newFilter = addFilter(newConfigId);
      onRowCreated?.(newFilter.id);
      if (onCancel) {
        onCancel();
      }
    }
  };

  const handleOperationChange = (operation: string) => {
    if (filterId && filter) {
      const updates = registry.handleOperationChange(filter, operation);
      updateFilter(filterId, updates);
      if (trackingId) {
        amplitude?.onFilterOperatorSelected?.({
          filterRowId: trackingId,
          filterType: filter.type,
          operator: operation,
        });
      }
    }
  };

  const handleDelete = () => {
    if (trackingId) {
      amplitude?.onFilterRowDeleted?.({ filterRowId: trackingId, filterType: filter?.type });
    }
    onRemove?.();
    if (filterId && filter) {
      removeFilter(filterId);
    } else if (onCancel) {
      onCancel();
    }
  };

  const handleUpdate = (updates: Partial<BaseFilterInput>) => {
    if (filterId && filter) {
      updateFilter(filterId, updates);
      if (!hasEnteredValueRef.current && trackingId) {
        hasEnteredValueRef.current = true;
        amplitude?.onFilterValueEntered?.({ filterRowId: trackingId, filterType: filter.type });
      }
    }
  };

  const renderDefaultValue = (value: unknown) => {
    if (!value) {
      return (
        <DashboardTypography variant="body2" vibe="text-neutral-quiet">
          {t("dashboard", "ContactFiltering.common.choose")}
        </DashboardTypography>
      );
    }
  };

  const renderOperationValue = (value: unknown) => {
    if (!value) {
      return (
        <DashboardTypography variant="body2" vibe="text-neutral-quiet">
          {t("dashboard", "ContactFiltering.operations.is")}
        </DashboardTypography>
      );
    }

    return (
      filterConfig?.getOperationLabel?.(String(value)) ??
      t("dashboard", `ContactFiltering.operations.${value as string}` as never)
    );
  };

  const filterConfig = filter ? registry.get(filter.configId) : null;

  useEffect(() => {
    if (filter && filterId) {
      const isValid = registry.validate(filter);

      if (isValid && !previousValidState.current && trackingId) {
        amplitude?.onFilterApplied?.({
          filterType: filter.type,
          filterOperation: filter.operation,
          validFilterCount: validFilters.length,
          combinator: filterCombinator,
          filterRowId: trackingId,
        });
      }

      previousValidState.current = isValid;
    }
  }, [
    filter,
    filterId,
    filters.length,
    filterCombinator,
    searchQuery,
    validFilters.length,
    registry,
    amplitude,
    trackingId,
  ]);

  const isInitialFilter = Boolean(filter && filter.id && isInitialFilterId(filter.id));
  const shouldOpenOperationByDefault = !isInitialFilter && Boolean(filterConfig?.openOperationsByDefault);

  useEffect(() => {
    if (shouldOpenOperationByDefault && trackingId && filter) {
      amplitude?.onFilterOperatorDropdownOpened?.({ filterRowId: trackingId, filterType: filter.type });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scoped to configId transitions only, not every filter/amplitude churn
  }, [filter?.configId]);

  return (
    <FilterRowLayout
      key={filter ? filter.id + filter.configId : undefined}
      slots={{
        type: (
          <FilterTypeSelector
            filterConfigId={filter?.configId}
            onTypeChange={handleTypeChange}
            defaultOpen={!filter?.configId}
            onDropdownOpened={() => {
              if (trackingId) {
                amplitude?.onFilterTypeDropdownOpened?.({ filterRowId: trackingId });
              }
            }}
            onCategorySelected={({ category, opensSubmenu }) => {
              if (trackingId) {
                amplitude?.onFilterCategorySelected?.({ filterRowId: trackingId, category, opensSubmenu });
              }
            }}
            onSubmenuItemSelected={({ category, filterType }) => {
              if (trackingId) {
                amplitude?.onFilterSubmenuItemSelected?.({ filterRowId: trackingId, category, filterType });
              }
            }}
          />
        ),
        operation: (
          <DashboardSelect
            key={filter ? filter.id + filter.configId : undefined}
            value={filter?.operation}
            onChange={(e: SelectChangeEvent<unknown>) => handleOperationChange(e.target.value as string)}
            onOpen={() => {
              if (trackingId && filter) {
                amplitude?.onFilterOperatorDropdownOpened?.({ filterRowId: trackingId, filterType: filter.type });
              }
            }}
            disabled={!filter?.configId}
            size="medium"
            sx={{ width: "100%" }}
            data-test="filter-operation-select"
            renderValue={renderOperationValue}
            displayEmpty
            defaultOpen={shouldOpenOperationByDefault}
          >
            {filter && filterConfig && !filterConfig.operations.some(({ value }) => value === filter.operation) && (
              <DashboardMenuItem value={filter.operation} disabled sx={{ display: "none" }}>
                {renderOperationValue(filter.operation)}
              </DashboardMenuItem>
            )}
            {filterConfig?.operations.map((op) => (
              <DashboardMenuItem key={op.value} value={op.value}>
                {filterConfig.getOperationLabel?.(op.value) ??
                  t("dashboard", `ContactFiltering.operations.${op.value}` as never)}
              </DashboardMenuItem>
            ))}
          </DashboardSelect>
        ),
        values:
          filter && filterConfig ? (
            <Box
              data-filter-id={filter.id}
              sx={{ width: "100%" }}
              onFocusCapture={() => {
                if (!hasOpenedValueControlRef.current && trackingId) {
                  hasOpenedValueControlRef.current = true;
                  amplitude?.onFilterValueControlOpened?.({ filterRowId: trackingId, filterType: filter.type });
                }
              }}
            >
              <filterConfig.ValueComponent filter={filter} onUpdate={handleUpdate} isInitialFilter={isInitialFilter} />
            </Box>
          ) : (
            <DashboardSelect
              disabled
              size="medium"
              sx={{ flex: 1 }}
              data-test="filter-value-select-disabled"
              displayEmpty
              renderValue={renderDefaultValue}
            >
              <DashboardMenuItem value="">{t("dashboard", "ContactFiltering.common.choose")}</DashboardMenuItem>
            </DashboardSelect>
          ),
        delete: !isReservedList ? (
          <DashboardIconButton
            onClick={handleDelete}
            size="medium"
            vibe="neutral"
            variant="text"
            data-test="filter-delete-button"
            aria-label={t("dashboard", "common.delete")}
          >
            <Delete />
          </DashboardIconButton>
        ) : null,
      }}
    />
  );
};
